import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, MessageSquare, ImagePlus, X } from "lucide-react";
import { fileToDataUrl } from "@/lib/image";

const API = "/api";
const headers = () => ({
  Authorization: `Bearer ${localStorage.getItem("atr_token") || sessionStorage.getItem("atr_admin_token") || ""}`,
});

export const AdminSupport = () => {
  const [threads, setThreads] = useState(null);
  const [activeEmail, setActiveEmail] = useState(null);
  const [messages, setMessages] = useState(null);
  const [text, setText] = useState("");
  const [image, setImage] = useState(null);
  const [sending, setSending] = useState(false);
  const bodyRef = useRef(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/admin/support`, { headers: headers() });
    setThreads(data.items);
  }, []);

  const loadMessages = useCallback(async (email) => {
    if (!email) return;
    const { data } = await axios.get(`${API}/admin/support/${encodeURIComponent(email)}/messages`, { headers: headers() });
    setMessages(data.items);
  }, []);

  useEffect(() => {
    load().catch(() => {});
    const t = setInterval(() => {
      load().catch(() => {});
      loadMessages(activeEmail).catch(() => {});
    }, 10000);
    return () => clearInterval(t);
  }, [load, loadMessages, activeEmail]);

  const openThread = (email) => {
    setActiveEmail(email);
    setMessages(null);
    loadMessages(email).catch(() => {});
    axios.post(`${API}/admin/support/${encodeURIComponent(email)}/read`, {}, { headers: headers() }).catch(() => {});
  };

  const active = threads?.find((t) => t.email === activeEmail) || null;

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, activeEmail]);

  const pickImage = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      const dataUrl = await fileToDataUrl(f);
      if (dataUrl.length > 880_000) {
        toast.error("Gambar terlalu besar, pilih yang lebih kecil");
        return;
      }
      setImage(dataUrl);
    } catch {
      toast.error("File bukan gambar yang valid");
    }
  };

  const reply = async (e) => {
    e.preventDefault();
    if ((!text.trim() && !image) || !activeEmail) return;
    setSending(true);
    try {
      await axios.post(`${API}/admin/support/${encodeURIComponent(activeEmail)}/reply`,
        { message: text.trim(), image }, { headers: headers() });
      setText("");
      setImage(null);
      loadMessages(activeEmail);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengirim balasan");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid gap-3 md:grid-cols-2" data-testid="admin-support">
      <div className="border border-white/10 bg-[#0d1219]">
        <p className="border-b border-white/10 px-4 py-3 font-mono text-[9px] uppercase tracking-[0.25em] text-mute-dim">
          Percakapan User {threads ? `(${threads.length})` : ""}
        </p>
        <div className="max-h-[28rem] overflow-y-auto" data-testid="support-thread-list">
          {threads === null ? (
            <p className="px-4 py-8 text-center font-mono text-xs text-mute-dim">memuat...</p>
          ) : threads.length === 0 ? (
            <p className="px-4 py-8 text-center font-mono text-xs text-mute-dim">belum ada pesan dari user</p>
          ) : (
            threads.map((t) => (
              <button key={t.email} onClick={() => openThread(t.email)} data-testid={`support-thread-${t.email.split("@")[0]}`}
                className={`flex w-full items-center justify-between gap-2 border-b border-white/5 px-4 py-3 text-left transition-colors hover:bg-white/[0.04] ${activeEmail === t.email ? "bg-white/[0.05]" : ""}`}>
                <div className="min-w-0">
                  <p className="truncate font-mono text-xs font-bold text-white">{t.name || "—"} <span className="font-normal text-mute-dim">{t.email}</span></p>
                  <p className="mt-0.5 truncate font-mono text-[10px] text-mute">{t.last_message || "—"}</p>
                </div>
                {t.unread > 0 && (
                  <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-sell px-1.5 font-mono text-[9px] font-bold text-white" data-testid={`support-unread-${t.email.split("@")[0]}`}>
                    {t.unread}
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      </div>

      <div className="flex flex-col border border-white/10 bg-[#0d1219]">
        <p className="border-b border-white/10 px-4 py-3 font-mono text-[9px] uppercase tracking-[0.25em] text-mute-dim">
          {active ? `Balas ke ${active.name || active.email}${active.phone ? ` · +${active.phone}` : ""}` : "Pilih percakapan"}
        </p>
        {!active ? (
          <div className="flex flex-1 items-center justify-center py-16 text-mute-dim">
            <MessageSquare size={22} />
          </div>
        ) : (
          <>
            <div ref={bodyRef} className="h-80 flex-1 space-y-2 overflow-y-auto p-4" data-testid="admin-support-messages">
              {messages === null ? (
                <p className="py-8 text-center font-mono text-xs text-mute-dim">memuat pesan...</p>
              ) : (
                messages.map((m) => (
                  <div key={m.id} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] border px-3 py-2 font-mono text-xs leading-relaxed ${
                      m.sender === "admin" ? "border-buy/30 bg-buy/10 text-white" : "border-white/15 bg-white/5 text-white"
                    }`}>
                      {m.sender === "user" && <p className="mb-0.5 text-[8px] font-bold uppercase tracking-widest text-mute-dim">User</p>}
                      {m.image && (
                        <img src={m.image} alt="lampiran" data-testid={`admin-support-image-${m.id?.slice(0, 8)}`}
                          className="mb-1 max-w-full border border-white/10" />
                      )}
                      {m.message && m.message !== "[gambar]" && <span>{m.message}</span>}
                      <p className="mt-1 text-right text-[8px] text-mute-dim">{m.created_at?.slice(5, 16).replace("T", " ")}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
            {image && (
              <div className="flex items-center gap-2 border-t border-white/10 px-3 py-2" data-testid="admin-image-preview">
                <img src={image} alt="pratinjau" className="h-12 border border-white/15" />
                <button onClick={() => setImage(null)} data-testid="admin-image-remove"
                  className="text-mute-dim transition-colors hover:text-sell">
                  <X size={14} />
                </button>
              </div>
            )}
            <form onSubmit={reply} className="flex gap-2 border-t border-white/10 p-3">
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickImage} data-testid="admin-image-input" />
              <button type="button" onClick={() => fileRef.current?.click()} data-testid="admin-attach-button" title="Kirim gambar (QRIS/rekening)"
                className="flex h-9 w-10 shrink-0 items-center justify-center border border-white/15 text-mute transition-colors hover:border-buy/60 hover:text-buy">
                <ImagePlus size={14} />
              </button>
              <input value={text} onChange={(e) => setText(e.target.value)} maxLength={1000}
                placeholder="tulis balasan..." data-testid="support-reply-input"
                className="h-9 min-w-0 flex-1 border border-white/15 bg-ink px-3 font-mono text-xs text-white outline-none focus:border-buy/60" />
              <button type="submit" disabled={sending || (!text.trim() && !image)} data-testid="support-reply-send"
                className="flex h-9 w-10 shrink-0 items-center justify-center border border-buy bg-buy/10 text-buy transition-colors hover:bg-buy hover:text-ink disabled:opacity-50">
                <Send size={14} />
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};
